/**
 * ER-08 — a file handed to EmmA in the drawer's normal chat (emma-hub PR #978 is the Brain's half).
 *
 * The Brain keeps the bytes as a private ER-05 revision bound to the conversation BY ID, and reads
 * every attached file's CONTENTS to her on every later turn by itself — the ids a message carries only
 * say which turn the file came with, so a reopened conversation shows the chip on the right bubble.
 * Nothing here is a URL she is given: a model handed a link reads nothing.
 *
 * This module is the CRM's side, kept out of the component so it runs under `node --test`: what is
 * refused before a byte leaves the browser (the Brain's own limits, so the sentence arrives at once and
 * the upload is saved), the upload, the list, the shape a turn's files must have, and reopening one.
 * Every call goes through nginx (`/emma-attachments/`, `/emma-artifacts/` in `nginx.conf.template`),
 * which forwards the caller's bearer exactly as `/emma-history/` does — the headers are a REQUIRED
 * argument for the same reason they are on `loadEmmaHistory`: a call site cannot forget them and compile.
 */
import {EmmaReadError, authedJson} from './emma-chat';
import {isTauri} from './desktop';

/** The Brain's limits (emma-hub `lib/agent/attachments.ts`), mirrored so a refusal costs no upload. */
export const MAX_ATTACHMENT_BYTES = 8_000_000;
export const MAX_ATTACHMENTS_PER_CONVERSATION = 12;
/** What the file picker offers. HEIC is deliberately absent: no reader on her side opens it. */
export const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp,text/plain,text/csv,text/markdown,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.md';
const MIMES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'text/plain', 'text/csv', 'text/markdown']);
const BY_EXT: Record<string, string> = {pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', txt: 'text/plain', csv: 'text/csv', md: 'text/markdown'};

/** What a turn carries, and what a chip needs: enough to name the file and reopen the exact revision. */
export type TurnFile = {revision_id: string; name: string; mime: string; size_bytes: number};
/** One file bound to a conversation, as the Brain lists it. `read_note` says what she made of it. */
export type Attachment = TurnFile & {id: string; read_by?: string | null; read_note?: string | null};

/** The browser's type when it has one; the name's extension when it sends none or `octet-stream`. */
export function mimeOfFile(f: {type?: string; name: string}): string {
  const t = (f.type ?? '').toLowerCase();
  if (t && t !== 'application/octet-stream') return t === 'image/jpg' ? 'image/jpeg' : t;
  return BY_EXT[f.name.split('.').pop()?.toLowerCase() ?? ''] ?? t;
}

/**
 * Why a file will not be sent — a CODE and its words' parameters, so the drawer says it in the staff
 * member's language (`emma.attachRefuse.*`), never an English sentence baked in here. `null` means it goes.
 */
export type Refusal =
  | {code: 'type'; mime: string; heic: boolean}
  | {code: 'empty'}
  | {code: 'big'; mb: string; max: number}
  | {code: 'many'; max: number};

export function refusalFor(f: {name: string; size: number; type?: string}, alreadyAttached = 0): Refusal | null {
  if (alreadyAttached >= MAX_ATTACHMENTS_PER_CONVERSATION) return {code: 'many', max: MAX_ATTACHMENTS_PER_CONVERSATION};
  const mime = mimeOfFile(f);
  if (!MIMES.has(mime)) return {code: 'type', mime, heic: /heic|heif/i.test(mime) || /\.hei[cf]$/i.test(f.name)};
  if (f.size <= 0) return {code: 'empty'};
  if (f.size > MAX_ATTACHMENT_BYTES) return {code: 'big', mb: (f.size / 1_000_000).toFixed(1), max: MAX_ATTACHMENT_BYTES / 1_000_000};
  return null;
}

/** A size a person reads: "12 KB", "2.4 MB" — never a raw byte count on a chip. */
export const fmtSize = (n: number): string => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);
/** The kind on a chip. Translated by the screen (`emma.attachKind.*`); this is the id, not the word. */
export const kindOf = (mime: string): 'pdf' | 'photo' | 'text' => (mime === 'application/pdf' ? 'pdf' : mime.startsWith('image/') ? 'photo' : 'text');

/**
 * WHAT A TURN'S FILE IS, ENFORCED. The same rule `readTurns` applies to a turn: a row the chip cannot
 * draw or reopen (no revision id, no name) is dropped here, so the renderer never defends itself.
 */
export function readTurnFiles(raw: unknown): TurnFile[] {
  if (!Array.isArray(raw)) return [];
  const out: TurnFile[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const f = row as Record<string, unknown>;
    const revision_id = typeof f.revision_id === 'string' ? f.revision_id : '';
    const name = typeof f.name === 'string' ? f.name.trim() : '';
    if (!revision_id || !name) continue;
    const size = Number(f.size_bytes);
    out.push({revision_id, name, mime: typeof f.mime === 'string' ? f.mime : '', size_bytes: Number.isFinite(size) ? size : 0});
  }
  return out;
}

/** The list shape: a turn file plus the binding's own id (falls back to the revision id). */
export function readAttachments(raw: unknown): Attachment[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    const [file] = readTurnFiles([row]);
    if (!file) return [];
    const a = row as Record<string, unknown>;
    return [{
      ...file,
      id: typeof a.id === 'string' && a.id ? a.id : file.revision_id,
      read_by: typeof a.read_by === 'string' ? a.read_by : null,
      read_note: typeof a.read_note === 'string' ? a.read_note : null,
    }];
  });
}

const sessionPath = (sessionId: string) => `/emma-attachments/${encodeURIComponent(sessionId)}/attachments`;

/**
 * Hand the Brain one file for this conversation. `job_ref` scopes the stored artifact to that job when
 * the drawer is open on one. The refusal the server sends (`{ok:false, error}`) is a sentence written
 * for the person and is thrown VERBATIM with its status — the drawer shows it as it came.
 *
 * No `content-type` header: the browser writes the multipart boundary itself, and setting one by hand
 * is the classic way to send a body the server cannot parse.
 */
export async function uploadAttachment(
  sessionId: string,
  file: File,
  headers: Record<string, string>,
  jobRef?: string | null,
  refreshAuth?: () => Promise<Record<string, string> | null>,
): Promise<Attachment> {
  const once = async (h: Record<string, string>) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (jobRef) fd.append('job_ref', jobRef);
    const r = await fetch(sessionPath(sessionId), {method: 'POST', headers: {...h, accept: 'application/json'}, body: fd});
    const body = (await r.json().catch(() => null)) as Record<string, unknown> | null;
    return {status: r.status, ok: r.ok, body};
  };
  let res = await once(headers);
  if (res.status === 401 && refreshAuth) {
    const fresh = await refreshAuth().catch(() => null);
    if (fresh) res = await once({...headers, ...fresh});
  }
  const [attachment] = res.body?.ok ? readAttachments([res.body.attachment]) : [];
  if (!res.ok || !attachment) {
    throw new EmmaReadError(String(res.body?.error ?? `The file was not kept (${res.status})`), res.status);
  }
  return attachment;
}

/** Every file bound to the conversation, whichever turn (or none yet) it came with. */
export async function listAttachments(
  sessionId: string,
  headers: Record<string, string>,
  refreshAuth?: () => Promise<Record<string, string> | null>,
): Promise<Attachment[]> {
  const body = await authedJson(sessionPath(sessionId), headers, 'Attachments unavailable', refreshAuth);
  return readAttachments((body as {attachments?: unknown}).attachments);
}

/** The files on the conversation that no message has carried yet: offered again in the tray. */
export function notYetHanded(all: Attachment[], turnsFiles: TurnFile[][]): Attachment[] {
  const handed = new Set(turnsFiles.flat().map((f) => f.revision_id));
  return all.filter((a) => !handed.has(a.revision_id));
}

/**
 * Reopen the exact revision in a new tab. The Brain streams the bytes after its own access check
 * (`/emma-artifacts/<revision>?bytes=1`), so the bearer travels with the fetch and the tab shows a
 * blob of them — a bare link to the API would open to 401.
 *
 * THE TAB IS OPENED BEFORE THE FETCH. A `window.open` that happens after an `await` is no longer in
 * the click's hand and the browser blocks it as a popup; opened first, it is the click's own tab and
 * only its address changes once the bytes are in.
 *
 * Where no tab can be had the bytes still reach him: the desktop shell (WKWebView has no tab and
 * answers `null`) routes a `blob:` through its bridge to the Mac's viewer (`lib/desktop.ts`), and a
 * browser that blocked the popup is handed the file to save instead — measured in the app's own
 * browser pane, where `window.open` answers `null` and a chip click did nothing at all.
 */
export async function openAttachment(f: TurnFile, headers: Record<string, string>): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  try {
    const r = await fetch(`/emma-artifacts/${encodeURIComponent(f.revision_id)}?bytes=1`, {headers});
    if (!r.ok) {
      const body = (await r.json().catch(() => null)) as {error?: string} | null;
      throw new EmmaReadError(String(body?.error ?? `The file could not be opened (${r.status})`), r.status);
    }
    const raw = await r.blob();
    const blob = raw.type ? raw : new Blob([raw], {type: f.mime || 'application/octet-stream'});
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else if (isTauri()) window.open(url, '_blank');
    else saveFile(url, f.name);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** A download in the click's own document — needs no popup permission. */
function saveFile(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
